import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { FileCheck2, Loader2, ShieldCheck, CreditCard, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { SITE, ONLINE_PAYMENTS_ENABLED } from "../mock";
import { DobPicker } from "./Booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNIT_PRICE = 35;

export default function CertifiedCopy() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [dob, setDob] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(null); // {code, percent}
  const [applying, setApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const subtotal = UNIT_PRICE * quantity;
  const discountAmount = discount ? Math.round(subtotal * discount.percent) / 100 : 0;
  const base = Math.round((subtotal - discountAmount) * 100) / 100;
  const total = Math.round(base * 1.02 * 100) / 100;
  const fee = Math.round((total - base) * 100) / 100;

  const applyDiscount = async () => {
    const code = discountCode.trim();
    if (!code) { toast.error("Please enter a discount code."); return; }
    setApplying(true);
    try {
      const res = await axios.get(`${API}/copy-requests/validate-discount`, { params: { code } });
      setDiscount(res.data);
      toast.success(`Discount applied — ${res.data.percent}% off!`);
    } catch {
      setDiscount(null);
      toast.error("Invalid discount code.");
    } finally {
      setApplying(false);
    }
  };

  const submit = async () => {
    if (!form.full_name.trim()) { toast.error("Please enter the testee's full name."); return; }
    if (!dob) { toast.error("Please select the testee's date of birth."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error("Please enter a valid email address."); return; }
    if (!form.phone.trim()) { toast.error("Please enter a contact phone number."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/copy-requests`, {
        full_name: form.full_name.trim(),
        dob: format(dob, "MM/dd/yyyy"),
        email: form.email.trim(),
        phone: form.phone.trim(),
        quantity,
        discount_code: discount?.code || null,
      });
      const { gateway_url, fields } = res.data;
      const f = document.createElement("form");
      f.method = "POST";
      f.action = gateway_url;
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = k;
        input.value = String(v);
        f.appendChild(input);
      });
      document.body.appendChild(f);
      f.submit();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start your request. Please try again or call us.");
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F6FAFB] min-h-screen" data-testid="certified-copy-page">
      <div className="bg-[#0A2540] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[#39C1D0] font-bold text-sm uppercase tracking-widest mb-3">Additional Services</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">Certified Copy of Test Results</h1>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Request an official certified copy of your TOEIC® score report and pay securely online.
            We'll contact you as soon as it's ready for pickup at our George Town office.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
        {!ONLINE_PAYMENTS_ENABLED ? (
          <Card className="rounded-2xl border-slate-100 shadow-sm max-w-2xl mx-auto" data-testid="copy-requests-unavailable">
            <CardContent className="p-10 text-center">
              <div className="w-14 h-14 rounded-xl bg-[#E8F7F9] border border-[#B5E6EC] flex items-center justify-center mx-auto mb-5">
                <FileCheck2 className="text-[#0E9AAA]" size={26} />
              </div>
              <h2 className="font-extrabold text-[#0A2540] text-xl">Online Requests Temporarily Unavailable</h2>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                We're briefly pausing online requests and payments. To order a certified copy of test results
                (CI $35.00 per copy), please call us at <strong>{SITE.phone}</strong> or email{" "}
                <strong>{SITE.email}</strong> — we'll be happy to assist.
              </p>
              <p className="mt-4 text-xs text-slate-500 flex items-center justify-center gap-1.5">
                <MapPin size={13} className="text-[#0E9AAA]" /> {SITE.address} — {SITE.addressNote}. {SITE.hours}.
              </p>
            </CardContent>
          </Card>
        ) : (
        <div className="grid lg:grid-cols-5 gap-8">
          <Card className="rounded-2xl border-slate-100 shadow-sm lg:col-span-3">
            <CardContent className="p-8">
              <div className="flex items-center gap-2.5 mb-6">
                <FileCheck2 className="text-[#0E9AAA]" size={20} />
                <h2 className="font-extrabold text-[#0A2540] text-lg">Request Details</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <Label className="font-bold text-[#0A2540]">Testee Full Name *</Label>
                  <Input data-testid="copy-name-input" value={form.full_name} onChange={set("full_name")}
                    placeholder="Full name exactly as it appeared on the test" className="mt-1.5 rounded-lg" />
                </div>
                <div>
                  <Label className="font-bold text-[#0A2540]">Date of Birth *</Label>
                  <DobPicker value={dob} onChange={setDob} testId="copy-dob-picker" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="font-bold text-[#0A2540]">Email *</Label>
                    <Input data-testid="copy-email-input" type="email" value={form.email} onChange={set("email")}
                      placeholder="you@example.com" className="mt-1.5 rounded-lg" />
                  </div>
                  <div>
                    <Label className="font-bold text-[#0A2540]">Phone *</Label>
                    <Input data-testid="copy-phone-input" value={form.phone} onChange={set("phone")}
                      placeholder="345-000-0000" className="mt-1.5 rounded-lg" />
                  </div>
                </div>
                <div>
                  <Label className="font-bold text-[#0A2540]">Number of Copies *</Label>
                  <Select value={String(quantity)} onValueChange={(v) => setQuantity(Number(v))}>
                    <SelectTrigger data-testid="copy-qty-select" className="mt-1.5 rounded-lg w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "copy" : "copies"}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-6 flex items-start gap-1.5">
                <MapPin size={13} className="mt-0.5 shrink-0 text-[#0E9AAA]" />
                Pickup at {SITE.address} — {SITE.addressNote}. {SITE.hours}. Bring a valid photo ID when collecting.
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-slate-100 shadow-sm lg:col-span-2 h-fit">
            <CardContent className="p-8">
              <h2 className="font-extrabold text-[#0A2540] text-lg mb-5">Order Summary</h2>
              <div className="mb-4">
                <Label className="font-bold text-[#0A2540] text-sm">Discount Code</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    data-testid="copy-discount-input"
                    value={discountCode}
                    onChange={(e) => { setDiscountCode(e.target.value); if (discount) setDiscount(null); }}
                    placeholder="Enter code"
                    className="rounded-lg"
                  />
                  <Button
                    data-testid="copy-discount-apply-btn"
                    variant="outline"
                    onClick={applyDiscount}
                    disabled={applying}
                    className="rounded-lg font-bold border-[#0E9AAA] text-[#0E9AAA] hover:bg-[#E8F7F9] hover:text-[#0C8695] shrink-0"
                  >
                    {applying ? <Loader2 size={15} className="animate-spin" /> : "Apply"}
                  </Button>
                </div>
                {discount && (
                  <p className="text-xs text-emerald-600 font-semibold mt-1.5" data-testid="copy-discount-applied">
                    Code "{discount.code}" applied — {discount.percent}% off
                  </p>
                )}
              </div>
              <div className="bg-[#F6FAFB] rounded-lg p-4 text-sm space-y-1.5 mb-5">
                <div className="flex justify-between text-slate-600">
                  <span>{quantity} × Certified copy</span><span className="font-semibold">CI ${subtotal.toFixed(2)}</span>
                </div>
                {discount && (
                  <div className="flex justify-between text-emerald-600">
                    <span>Discount ({discount.percent}% off)</span><span className="font-semibold">− CI ${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-slate-600">
                  <span>Online transaction fee (2%)</span><span className="font-semibold">CI ${fee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-extrabold text-[#0A2540] pt-1.5 border-t border-slate-200">
                  <span>Total</span><span data-testid="copy-total">CI ${total.toFixed(2)}</span>
                </div>
              </div>
              <Button data-testid="copy-submit-btn" onClick={submit} disabled={submitting}
                className="w-full bg-[#0E9AAA] hover:bg-[#0C8695] text-white font-bold rounded-lg h-11 shadow-md">
                {submitting
                  ? <><Loader2 size={16} className="mr-2 animate-spin" /> Opening secure payment…</>
                  : <><CreditCard size={16} className="mr-2" /> Continue to Secure Payment</>}
              </Button>
              <p className="text-xs text-slate-600 mt-4 flex items-start gap-1.5">
                <ShieldCheck size={13} className="mt-0.5 shrink-0 text-[#0E9AAA]" />
                Online payment is required to submit this request. You will be redirected to our bank's secure payment
                page (Plug'n Pay via RBC). Your card details are never stored on this website.
              </p>
            </CardContent>
          </Card>
        </div>
        )}
      </div>
    </div>
  );
}
